import {i18n} from "./i18n";
import * as mdc from "material-components-web";
import {list} from "material-components-web";
import {TenantTerminationState} from "./tenant";
import UrlHelpers from "./url";
import {addIcon, deleteIcon, permIcon, serverIcon} from "./icons";

export class OrganizationV2 {
	private readonly tenantListId = 'tenantList';
	private readonly isOrgaAdmin:boolean;
	private readonly isCloudAdmin:boolean;
	private readonly organizationId:string;
	private _dapi: Dapi = (<any>window).dapi;
	private _i18n: i18n = new i18n();

	private _mdcLists: { [id: string]: mdc.list.MDCList } = {};
	private _mdcProgressBars: { [id: string]: mdc.linearProgress.MDCLinearProgress } = {};
	private _mdcDialogs: { [id: string]: mdc.dialog.MDCDialog } = {};
	private _mdcSnackbars: { [id: string]: mdc.snackbar.MDCSnackbar } = {};

	constructor() {
		this.isCloudAdmin = !!UrlHelpers.getMetaData("isCloudAdmin");
		this.isOrgaAdmin = this.isCloudAdmin ? this.isCloudAdmin : !!UrlHelpers.getMetaData('isAdmin');

		this.organizationId = UrlHelpers.getMetaData('organizationId');

		this.addDapiContextActions();
		this.loadMaterial();
		this.setupTenantList();

		this.registerStorageListener();

		const tenantListUrl: string = UrlHelpers.getLinkRelHref('tenants');
		this.loadTenantList(tenantListUrl, this.writeDataToElement, this.displayErrorMessage);

		const btnLoadMore = <HTMLButtonElement>document.getElementById('btnLoadMoreTenants');
		btnLoadMore.addEventListener('click', this.handleBtnLoadMoreClick);
	}

	private registerStorageListener = () => {
		window.addEventListener('storage', (e:StorageEvent) => {
			const storageKey = "createTenantForOrganization";
			if (e.key == storageKey) {
				const value = e.newValue;
				if (value && value == this.organizationId) {
					window.location.reload();
				}
			}
		})
	};

	private addDapiContextActions = () => {
		if (!this.isCloudAdmin && !this.isOrgaAdmin) {
			return
		}

		const ctxActions = new Array<IContextAction>();

		ctxActions.push({
			title: this._i18n.getTranslation('organization_ctxaction_editorganization_title'),
			icon: serverIcon, //'https://s3.eu-central-1.amazonaws.com/dv-cloud-center/img/uEA1C-edit-32x32.png',
			href: UrlHelpers.getLinkRelHref('details')
		});

		const permIconUrl = permIcon;
		ctxActions.push({
			title: this._i18n.getTranslation('permission_organization_title'),
			icon: permIconUrl, //'https://s3.eu-central-1.amazonaws.com/dv-cloud-center/img/uEA6A-usergroup-32x32.png',
			href: UrlHelpers.getLinkRelHref('permission')
		});

		// todo: put organization id to query parameter
		const addIconUrl = addIcon;
		ctxActions.push({
			title: this._i18n.getTranslation('organization_ctxaction_newtenant_title'),
			icon: addIconUrl,
			callback: () => {
				const currentLocation = window.location;
				const subscriptionPath= '/center/subscriptionwizard';
				let subscriptionUrl = new URL(currentLocation.toString());
				subscriptionUrl.pathname = subscriptionPath;
				subscriptionUrl.searchParams.append('app', 'basis');
				subscriptionUrl.searchParams.append('oid', this.organizationId);

				window.open(subscriptionUrl.toString(),'_blank');
			}
		});

		if(this.isCloudAdmin) {
			const deleteIconUrl = deleteIcon;
			ctxActions.push({
				title: this._i18n.getTranslation('organization_ctx_action_btn_delete_organization_label'), //'Organisation löschen',
				icon: deleteIconUrl, //'https://s3.eu-central-1.amazonaws.com/dv-cloud-center/img/uEA14-delete-32x32.png',
				callback: () => {
					this.handleCtxActionDeleteOrga();
				}
			});
		}

		this._dapi.setContextActions(ctxActions);
	};

	private loadMaterial = () => {
		const uLists = document.querySelectorAll('.mdc-list');
		Array.apply(null, uLists).forEach((uList) => {
			const mdcList = list.MDCList.attachTo(uList);
			if (uList.id) {
				this._mdcLists[uList.id] = mdcList;
			}
		});

		const progressBars = document.querySelectorAll('.mdc-linear-progress');
		Array.apply(null, progressBars).forEach((pBar: HTMLElement) => {
			const mdcBar = mdc.linearProgress.MDCLinearProgress.attachTo(pBar);
			if (pBar.id) {
				this._mdcProgressBars[pBar.id] = mdcBar;
			}
		});

		const dialogs = document.querySelectorAll('.mdc-dialog');
		Array.apply(null, dialogs).forEach((d: HTMLElement) => {
			const mdcDialog = mdc.dialog.MDCDialog.attachTo(d);
			if (d.id) {
				if (d.id === 'modalDeleteOrganization') {
					mdcDialog.listen('MDCDialog:closed', this.handleDialogDeleteOrganizationClosed)
				}
				this._mdcDialogs[d.id] = mdcDialog;
			}
		});

		const snackbars = document.querySelectorAll('.mdc-snackbar');
		Array.apply(null, snackbars).forEach((snackbar) => {
			const mdcSnackbar = mdc.snackbar.MDCSnackbar.attachTo(snackbar);
			if (snackbar.id) {
				this._mdcSnackbars[snackbar.id] = mdcSnackbar;
			}
		});
	};

	private setupTenantList = (): void => {
		const list = this._mdcLists[this.tenantListId];
		if (!list) {
			console.error('tenant list missing');
			return;
		}

		const observedAttributeName = 'data-tenants';
		const uList = list.root;
		const observer = new MutationObserver(mutations => {
			mutations.forEach(mutation => {
				if (mutation.type == "attributes" && mutation.attributeName == observedAttributeName) {
					this.renderTenantList();
				}
			});
		});
		observer.observe(uList, {
			attributeFilter: [observedAttributeName],
		});

		this.resetTenantList();
		this.renderTenantList();
	};

	private resetTenantList = (): void => {
		const mdcList: mdc.list.MDCList = this._mdcLists[this.tenantListId];
		if (!mdcList) {
			console.log(`resetTenantList: cannot find mdcList with id ${this.tenantListId}`);
			return
		}

		mdcList.destroy();

		const newMdcList: mdc.list.MDCList = mdc.list.MDCList.attachTo(document.getElementById(this.tenantListId));
		newMdcList.listen('MDCList:action', this.handleTenantListItemClick);
	};

	private handleTenantListItemClick = (e: CustomEvent): void => {
		const index = e.detail.index;
		const list = this._mdcLists[this.tenantListId];
		const selectedItem = <HTMLLIElement>list.listElements[index];

		const href = selectedItem.dataset.href;
		if (!href){
			console.error("tenant url missing in element: ", selectedItem);
		}
		this._dapi.navigate(href);
	};

	private renderTenantList = (): void => {
		const data = this.readDataFromElement();
		if (!data) {
			console.log('renderTenantList: data missing');
			return
		}

		this.renderEmptyTenantList();
		this.renderListActions();

		if(!data.tenants || data.tenants.length === 0) {
			console.log('renderTenantList: tenant list empty');
			return
		}

		const ul = document.getElementById(this.tenantListId);
		if (!ul) {
			console.log(`renderTenantList: cannot find ul element with id ${this.tenantListId}`);
			return;
		}

		const collectionList = document.createElement('ul');

		data.tenants.forEach((tenant, index) => {
			const li = document.createElement('li');
			li.classList.add('mdc-list-item');
			li.dataset.tenantid = tenant.id;
			li.dataset.href = tenant._links['self'].href;

			const ripple = document.createElement('span');
			ripple.classList.add('mdc-list-item__ripple')

			li.appendChild(ripple);

			const isTenantTerminated: boolean = !(tenant.termination.state == TenantTerminationState.notTerminated);
			const icon = document.createElement('span');
			icon.classList.add('mdc-list-item__graphic', 'material-icons');
			icon.setAttribute('aria-hidden','true');

			if (isTenantTerminated) {
				icon.innerText = 'cloud_off';
				icon.style.color = '#ff0844';
			} else {
				icon.innerText = 'cloud';
			}

			li.appendChild(icon);

			li.insertAdjacentHTML('beforeend',
				`<span class="mdc-list-item__text">
                 <span class="mdc-list-item__primary-text">${tenant.name}</span>
                 <span class="mdc-list-item__secondary-text">https://${tenant.fullQualifiedDomain}</span>
             </span>`);

			collectionList.appendChild(li);
		});

		ul.innerHTML = collectionList.innerHTML;

		this.resetTenantList();
	};

	private renderEmptyTenantList = (): void => {
		const hideElementClass = 'hide-element';
		const id = "emptyTenantList";
		const el = <HTMLParagraphElement>document.getElementById(id);
		const data = this.readDataFromElement();
		if (!data.tenants || data.tenants.length === 0) {
			el.classList.remove(hideElementClass);
		} else {
			el.classList.add(hideElementClass);
		}
	};

	private renderListActions = (): void => {
		const hiddenClass = 'hide-element';
		const btnId = 'btnLoadMoreTenants';
		const btn = <HTMLButtonElement>document.getElementById(btnId);
		const parentDiv = <HTMLDivElement>btn.parentElement;
		if (this.areMoreTenantsToLoadExist()) {
			parentDiv.classList.remove(hiddenClass);
			return
		}
		parentDiv.classList.add(hiddenClass);
	};

	private areMoreTenantsToLoadExist = (): boolean => {
		const data = this.readDataFromElement();
		if (!data) {
			console.log("areMoreTenantsToLoadExist: cannot read data from element");
			return false;
		}
		if (!data.tenants || data.tenants.length === 0) {
			console.log('areMoreTenantsToLoadExist: tenant list empty');
			return false;
		}
		const more = !!data._links && !!data._links["more"] ? data._links["more"].href : null;
		if (!more) {
			return false;
		}
		return true;
	};

	private readDataFromElement = (): pagedTenantListDto => {
		const ul = document.getElementById(this.tenantListId);
		if (!ul) {
			console.error(`cannot find element with id ${this.tenantListId}`);
			return null;
		}
		const data = ul.dataset.tenants;
		if (!data) {
			return {
				tenants: []
			} as pagedTenantListDto;
		}
		return JSON.parse(data);
	};

	private writeDataToElement = (data: pagedTenantListDto): void => {
		const ul = document.getElementById(this.tenantListId);
		if (!ul) {
			console.error(`cannot find element with id ${this.tenantListId}`);
			return
		}
		ul.dataset.tenants = JSON.stringify(data);
	};

	private mergeData = (data: pagedTenantListDto): void => {
		if (!data) {
			console.log('mergeData: data is empty');
			return;
		}

		const current = this.readDataFromElement();
		current._links = data._links;

		if (!data.tenants) {
			console.log("mergeData: tenants is empty");
			this.writeDataToElement(current);
			return;
		}

		data.tenants.forEach(tenant => {
			current.tenants.push(tenant);
		});

		this.writeDataToElement(current);
	};

	private loadTenantList = (url: string, cbSuccess: (data: pagedTenantListDto) => void, cbError: (e) => void) => {
		this.openTenantLoadProgress();

		const req = new Request(url, {
			method: 'GET',
			headers: {'accept': 'application/hal+json'},
			credentials: 'same-origin'
		});

		fetch(req)
			.then(response => {
				this.closeTenantLoadProgress();
				if (response.ok) {
					response.json()
						.then((data:pagedTenantListDto) => {
							cbSuccess(data);
						})
						.catch(reason => {
							console.error(`load tenant data: cannot get json from response: ${reason}`);
							cbError(reason);
						});
				} else {
					response.text()
						.then(value => {
							console.error(`load tenant data failed with status ${response.status} - ${response.statusText}: ${value}`);
							cbError(`load tenant data failed`);
						})
						.catch(reason => {
							console.error(`load tenant data failed with status ${response.status} - ${response.statusText}: ${reason}`);
							cbError(`load tenant data failed`);
						});
				}
			})
			.catch(reason => {
				this.closeTenantLoadProgress();
				cbError(reason);
			});
	};

	private openTenantLoadProgress = () => {
		const id = 'loadListProgress';
		const progressBar = this._mdcProgressBars[id];
		progressBar.open();
	};

	private closeTenantLoadProgress = () => {
		const id = 'loadListProgress';
		const progressBar = this._mdcProgressBars[id];
		progressBar.close();
	};

	private displayErrorMessage = (message): void => {
		const id = 'organizationNotification';
		if (!this._mdcSnackbars.hasOwnProperty(id)) {
			console.error('mdcSnackbar not found');
			return;
		}
		const defaultCloseTimeoutValue = 10000;
		const disableCloseTimeoutValue = -1;
		const mdcSnackbar = this._mdcSnackbars[id];
		mdcSnackbar.labelText = message;
		mdcSnackbar.closeOnEscape = false;
		mdcSnackbar.timeoutMs = disableCloseTimeoutValue; //disableCloseTimer ? disableCloseTimeoutValue : defaultCloseTimeoutValue;
		mdcSnackbar.open();
	};

	private handleBtnLoadMoreClick = (e):void => {
		const data = this.readDataFromElement();
		if (!data) {
			console.log("areMoreTenantsToLoadExist: cannot read data from element");
			return
		}
		const more = !!data._links && !!data._links["more"] ? data._links["more"].href : null;
		if (!more) {
			return;
		}
		this.loadTenantList(more,this.mergeData, this.displayErrorMessage);
	};

	private handleCtxActionDeleteOrga = (): void => {
		const mdcDialog = this._mdcDialogs["modalDeleteOrganization"];
		mdcDialog.open();
	};

	private handleDialogDeleteOrganizationClosed = (e:CustomEvent): void => {
		if (e.detail.action === 'delete') {
			this.deleteOrganizationCall(() => {
				this._dapi.dispatchResourceEvent({
					name: "deleted",
					uri: window.location.pathname
				});
				this._dapi.closeResourceIfMainResource();
			}, this.displayErrorMessage);
		}
	};

	private deleteOrganizationCall = (cbSuccess: () => void, cbError: (msg) => void) => {
		const url = UrlHelpers.getLinkRelHref('self');
		const req = new Request(url, {
			method: 'DELETE',
			headers: {
				'accept':'application/json'
			},
			credentials: 'same-origin'
		});

		fetch(req)
			.then(response => {
				if (response.ok) {
					cbSuccess();
				} else if (response.status === 401) {
					cbError(this._i18n.getTranslation('organization_modal_delete_organization_error_not_allowed_message'));
				} else if (response.status === 409) {
					cbError(this._i18n.getTranslation('organization_modal_delete_organization_error_tenants_exists_message'));
				} else {
					cbError(this._i18n.getTranslation('organization_modal_delete_organization_error_unexpected_error_message'));
				}
			})
			.catch(reason => {
				console.error(`cannot execute delete organization call: ${reason}`);
				cbError(this._i18n.getTranslation('organization_modal_delete_organization_error_unexpected_error_message'));
			});
	};
}

export interface pagedTenantListDto {
	_links: { [key: string]: linkRel }
	tenants: tenantItemDto[]
}

export interface tenantItemDto {
	_links: { [key: string]: linkRel }
	id: string
	name: string
	fullQualifiedDomain: string
	termination: tenantTerminationDto
}

export interface tenantTerminationDto {
	state: TenantTerminationState
}

export interface linkRel {
	href: string
}
